import { useEffect, useMemo, useRef, useState, type UIEvent } from "react";
import { useSearchParams } from "react-router-dom";
import type {
  CommandItem,
  ExecuteCommandInput,
  FocusBucket,
  PortfolioView,
} from "@ceo-cmd/shared";
import { RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { UserMenu } from "@/components/user-menu";
import {
  useTodayExecute,
  useTodayQueue,
  useTodayRetryEffects,
  useTodayUndo,
} from "@/hooks/useToday";
import { ExecutiveSummaryStrip } from "@/components/today/executive-summary-strip";
import { FocusBucketSelector } from "@/components/today/focus-bucket-selector";
import { CommandItemCard } from "@/components/today/command-item-card";
import { CommandItemPanel } from "@/components/today/command-item-panel";
import { MobileFocusCard } from "@/components/today/mobile-focus-card";
import { MobileBucketPicker } from "@/components/today/mobile-bucket-picker";
import { PortfolioSelector } from "@/components/today/portfolio-selector";
import { TodayListControls } from "@/components/today/today-list-controls";
import { isFocusBucket } from "@/components/today/is-focus-bucket";
import {
  classifyAppliedEffects,
  classifySideEffects,
  type ClassifiedSideEffect,
} from "@/components/today/action-helpers";
import { BUCKET_LABELS } from "@/components/today/labels";
import {
  loadTodayPosition,
  patchTodayPosition,
  readTodayPosition,
  todayPositionFromSearch,
  todayPositionToSearch,
  writeTodayPosition,
} from "@/lib/today-position";
import {
  loadTodayListDensity,
  loadTodayPageSize,
  saveTodayListDensity,
  saveTodayPageSize,
  type TodayListDensity,
  type TodayPageSize,
} from "@/lib/today-list-prefs";

type ToastState = {
  kind: "success" | "partial" | "failure";
  title: string;
  effects: ClassifiedSideEffect[];
  undoItemId?: string;
  retryBody?: ExecuteCommandInput;
  retryItemId?: string;
  dispositionEventId?: string;
};

function pickNextSelectedId(
  previousItems: CommandItem[],
  completedId: string,
): string | null {
  const idx = previousItems.findIndex((i) => i.id === completedId);
  const remaining = previousItems.filter((i) => i.id !== completedId);
  if (remaining.length === 0) return null;
  if (idx < 0) return remaining[0]?.id ?? null;
  return remaining[idx]?.id ?? remaining[idx - 1]?.id ?? remaining[0]?.id ?? null;
}

const LIST_FOCUS_MARGIN_PX = 16;

function scrollCommandCardIntoView(
  list: HTMLElement,
  itemId: string,
  behavior: ScrollBehavior = "auto",
): boolean {
  const card = list.querySelector<HTMLElement>(
    `[data-command-id="${CSS.escape(itemId)}"]`,
  );
  if (!card) return false;

  const listRect = list.getBoundingClientRect();
  const cardRect = card.getBoundingClientRect();
  const comfortablyVisible =
    cardRect.top >= listRect.top + LIST_FOCUS_MARGIN_PX &&
    cardRect.bottom <= listRect.bottom - LIST_FOCUS_MARGIN_PX;
  if (comfortablyVisible) return true;

  const completelyHidden =
    cardRect.bottom <= listRect.top || cardRect.top >= listRect.bottom;

  let delta = 0;
  if (completelyHidden) {
    delta =
      cardRect.top -
      listRect.top -
      (listRect.height - cardRect.height) / 2;
  } else if (cardRect.top < listRect.top + LIST_FOCUS_MARGIN_PX) {
    delta = cardRect.top - listRect.top - LIST_FOCUS_MARGIN_PX;
  } else {
    delta = cardRect.bottom - listRect.bottom + LIST_FOCUS_MARGIN_PX;
  }

  const nextTop = Math.max(0, list.scrollTop + delta);
  list.scrollTo({ top: nextTop, behavior });
  return true;
}

export function HomePage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPosition = useRef(loadTodayPosition(searchParams)).current;
  const [bucket, setBucket] = useState<FocusBucket>(initialPosition.bucket);
  const [view, setView] = useState<PortfolioView>(initialPosition.view);
  const [companyId, setCompanyId] = useState<string | undefined>(
    initialPosition.companyId,
  );
  const [selectedId, setSelectedId] = useState<string | null>(
    initialPosition.selectedId,
  );
  const [pageSize, setPageSize] = useState<TodayPageSize>(() =>
    loadTodayPageSize(),
  );
  const [density, setDensity] = useState<TodayListDensity>(() =>
    loadTodayListDensity(),
  );
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);
  const itemsSnapshot = useRef<CommandItem[]>([]);
  const listRef = useRef<HTMLElement | null>(null);
  const listScrollRestored = useRef(false);
  const lastFocusedItemId = useRef<string | null>(null);
  const deepLinkBucketRetries = useRef(0);
  const queueScopeKey = `${bucket}:${view}:${companyId ?? ""}`;
  const previousQueueScope = useRef(queueScopeKey);

  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } =
    useTodayQueue(bucket, view, companyId);
  const execute = useTodayExecute(bucket, view, companyId);
  const retryEffects = useTodayRetryEffects(bucket, view, companyId);
  const undo = useTodayUndo(bucket, view, companyId);

  const items = data?.items ?? [];
  itemsSnapshot.current = items;

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageItems = items.slice(pageStart, pageStart + pageSize);

  const selected =
    items.find((o) => o.id === selectedId) ??
    pageItems[0] ??
    items[0] ??
    null;

  useEffect(() => {
    if (dataUpdatedAt) {
      setLastRefreshedAt(new Date(dataUpdatedAt).toISOString());
    }
  }, [dataUpdatedAt]);

  useEffect(() => {
    if (previousQueueScope.current !== queueScopeKey) {
      previousQueueScope.current = queueScopeKey;
      listScrollRestored.current = false;
      lastFocusedItemId.current = null;
      setPage(1);
      if (listRef.current) listRef.current.scrollTop = 0;
      patchTodayPosition({ listScrollTop: 0 });
    }
  }, [queueScopeKey]);

  useEffect(() => {
    if (!selectedId || items.length === 0) return;
    const idx = items.findIndex((item) => item.id === selectedId);
    if (idx < 0) return;
    const neededPage = Math.floor(idx / pageSize) + 1;
    if (neededPage !== page) setPage(neededPage);
  }, [selectedId, items, pageSize, page]);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  useEffect(() => {
    const previous = readTodayPosition();
    const position = {
      bucket,
      view,
      companyId: view === "company" ? companyId : undefined,
      selectedId,
      listScrollTop: previous?.listScrollTop ?? 0,
    };
    writeTodayPosition(position);
    const next = todayPositionToSearch(position);
    setSearchParams(
      (prev) => (prev.toString() === next.toString() ? prev : next),
      { replace: true },
    );
  }, [bucket, view, companyId, selectedId, setSearchParams]);

  useEffect(() => {
    if (isLoading || !data) return;

    if (!selectedId && items[0]) {
      setSelectedId(items[0].id);
      return;
    }
    if (
      selectedId &&
      items.length > 0 &&
      !items.some((o) => o.id === selectedId)
    ) {
      // Deep-link / Observability Open: item may live in another bucket.
      if (bucket !== "all_active" && deepLinkBucketRetries.current < 1) {
        deepLinkBucketRetries.current += 1;
        setBucket("all_active");
        return;
      }
      deepLinkBucketRetries.current = 0;
      listScrollRestored.current = false;
      lastFocusedItemId.current = null;
      if (listRef.current) listRef.current.scrollTop = 0;
      patchTodayPosition({ listScrollTop: 0 });
      setSelectedId(items[0]?.id ?? null);
      return;
    }
    if (selectedId && items.some((o) => o.id === selectedId)) {
      deepLinkBucketRetries.current = 0;
    }
    if (selectedId && items.length === 0) {
      setSelectedId(null);
    }
  }, [data, isLoading, items, selectedId, bucket]);

  // Apply Observability / external deep-links when the URL changes.
  useEffect(() => {
    const fromUrl = todayPositionFromSearch(searchParams);
    if (fromUrl.bucket && fromUrl.bucket !== bucket) {
      deepLinkBucketRetries.current = 0;
      setBucket(fromUrl.bucket);
    }
    if (
      fromUrl.selectedId &&
      fromUrl.selectedId !== selectedId &&
      searchParams.has("item")
    ) {
      deepLinkBucketRetries.current = 0;
      setSelectedId(fromUrl.selectedId);
    }
    // Only react to inbound URL changes (item/bucket), not our own replace writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional narrow deps
  }, [searchParams]);

  useEffect(() => {
    if (items.length === 0 || !listRef.current) return;

    const targetId = selectedId ?? selected?.id;
    if (!targetId) return;

    const node = listRef.current;
    const isInitialRestore = !listScrollRestored.current;
    const selectionChanged = lastFocusedItemId.current !== targetId;

    if (!isInitialRestore && !selectionChanged) return;

    const frame = window.requestAnimationFrame(() => {
      if (!listRef.current) return;

      if (isInitialRestore) {
        listRef.current.scrollTop = readTodayPosition()?.listScrollTop ?? 0;
      }

      scrollCommandCardIntoView(
        listRef.current,
        targetId,
        isInitialRestore ? "auto" : "smooth",
      );
      patchTodayPosition({ listScrollTop: listRef.current.scrollTop });
      listScrollRestored.current = true;
      lastFocusedItemId.current = targetId;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [items, selectedId, selected?.id, queueScopeKey]);

  function onListScroll(event: UIEvent<HTMLElement>) {
    patchTodayPosition({ listScrollTop: event.currentTarget.scrollTop });
  }

  function selectListItem(itemId: string) {
    setSelectedId(itemId);
    if (listRef.current) {
      scrollCommandCardIntoView(listRef.current, itemId, "smooth");
      patchTodayPosition({ listScrollTop: listRef.current.scrollTop });
      lastFocusedItemId.current = itemId;
    }
  }

  function changePage(nextPage: number) {
    const clamped = Math.min(Math.max(1, nextPage), totalPages);
    setPage(clamped);
    listScrollRestored.current = false;
    if (listRef.current) listRef.current.scrollTop = 0;
    patchTodayPosition({ listScrollTop: 0 });
    const start = (clamped - 1) * pageSize;
    const firstOnPage = items[start];
    if (firstOnPage) {
      setSelectedId(firstOnPage.id);
      lastFocusedItemId.current = firstOnPage.id;
    }
  }

  function changePageSize(size: TodayPageSize) {
    setPageSize(size);
    saveTodayPageSize(size);
    listScrollRestored.current = false;
    if (listRef.current) listRef.current.scrollTop = 0;
    patchTodayPosition({ listScrollTop: 0 });
  }

  function changeDensity(next: TodayListDensity) {
    setDensity(next);
    saveTodayListDensity(next);
  }

  const dateLabel = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
      }),
    [],
  );

  const activeQueueEmpty = (data?.movesToday ?? 0) === 0;
  const bucketEmpty = items.length === 0;
  const companyName =
    view === "company"
      ? data?.portfolio.companies.find((c) => c.id === companyId)?.name
      : undefined;

  async function onExecute(item: CommandItem, body: ExecuteCommandInput) {
    const previous = itemsSnapshot.current;
    const requestBody: ExecuteCommandInput = {
      ...body,
      idempotencyKey: body.idempotencyKey ?? crypto.randomUUID(),
    };
    try {
      const result = await execute.mutateAsync({
        itemId: item.id,
        body: requestBody,
      });
      const structured = classifyAppliedEffects(result.outcome.appliedEffects);
      const effects =
        structured ??
        classifySideEffects(item, result.outcome.sideEffectsApplied);
      const hasFailed =
        result.outcome.outcomeKind === "local_ok_partial_external" ||
        effects.some((e) => e.status === "failed");
      const nextId = pickNextSelectedId(previous, item.id);
      setSelectedId(nextId);

      if (hasFailed) {
        setToast({
          kind: "partial",
          title: `${result.outcome.message.replace(/\.$/, "")}, but an external update failed.`,
          effects,
          undoItemId: result.outcome.undoAvailableUntil
            ? item.id
            : undefined,
          retryItemId: item.id,
          retryBody: requestBody,
          dispositionEventId: result.outcome.dispositionEventId,
        });
      } else {
        setToast({
          kind: "success",
          title: localizeMessage(result.outcome.message, item),
          effects,
          undoItemId: result.outcome.undoAvailableUntil
            ? item.id
            : undefined,
        });
        window.setTimeout(() => setToast(null), 5000);
      }
    } catch {
      setToast({
        kind: "failure",
        title: "Could not record the decision. The item remains in your queue.",
        effects: [],
        retryItemId: item.id,
        retryBody: requestBody,
      });
    }
  }

  async function onUndo(itemId: string) {
    try {
      const result = await undo.mutateAsync(itemId);
      setToast({
        kind: "success",
        title: result.outcome.message,
        effects: [],
      });
      setSelectedId(itemId);
      window.setTimeout(() => setToast(null), 2500);
    } catch {
      setToast({
        kind: "failure",
        title: "Undo unavailable.",
        effects: [],
      });
    }
  }

  async function onRetryFailedEffects() {
    if (!toast?.dispositionEventId || !toast.retryItemId) return;
    try {
      const result = await retryEffects.mutateAsync({
        itemId: toast.retryItemId,
        dispositionEventId: toast.dispositionEventId,
      });
      const structured = classifyAppliedEffects(result.outcome.appliedEffects);
      const effects = structured ?? toast.effects;
      const hasFailed =
        result.outcome.outcomeKind === "local_ok_partial_external" ||
        effects.some((e) => e.status === "failed");
      if (hasFailed) {
        setToast({
          kind: "partial",
          title: "External update still failed after retry.",
          effects,
          undoItemId: result.outcome.undoAvailableUntil
            ? toast.retryItemId
            : toast.undoItemId,
          retryItemId: toast.retryItemId,
          dispositionEventId: toast.dispositionEventId,
        });
      } else {
        setToast({
          kind: "success",
          title: "Failed external updates retried successfully.",
          effects,
          undoItemId: result.outcome.undoAvailableUntil
            ? toast.retryItemId
            : toast.undoItemId,
        });
        window.setTimeout(() => setToast(null), 4000);
      }
    } catch {
      setToast({
        kind: "partial",
        title: "Could not retry external updates.",
        effects: toast.effects,
        undoItemId: toast.undoItemId,
        retryItemId: toast.retryItemId,
        dispositionEventId: toast.dispositionEventId,
      });
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-24 animate-pulse rounded-lg border border-border bg-card"
          />
        ))}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        Failed to load Today queue.
      </div>
    );
  }

  const focus = selected ?? items[0] ?? null;
  const moveIndex = focus
    ? Math.max(1, items.findIndex((i) => i.id === focus.id) + 1)
    : 1;
  const moveTotal = items.length;
  const refreshedLabel = lastRefreshedAt
    ? new Date(lastRefreshedAt).toLocaleTimeString()
    : new Date(data.sync.lastSyncedAt).toLocaleTimeString();

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground md:text-3xl">
            Today
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            CEO Command · {dateLabel}
            {user?.name ? ` · ${user.name}` : ""}
            {" · "}
            Last refreshed {refreshedLabel}
            {data.setup?.prototypeData ? (
              <span className="ml-1.5 rounded border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
                Prototype data
              </span>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PortfolioSelector
            portfolio={data.portfolio}
            view={view}
            companyId={companyId}
            compact
            onChange={(next) => {
              setView(next.view);
              setCompanyId(next.companyId);
              setSelectedId(null);
            }}
          />
          <button
            type="button"
            onClick={() => void refetch()}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-surface-hover"
          >
            <RefreshCw
              className={`size-3.5 ${isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
          <div className="hidden border-l border-border pl-3 md:block">
            <UserMenu />
          </div>
        </div>
      </header>

      {toast && (
        <ActionToast
          toast={toast}
          busy={
            execute.isPending || undo.isPending || retryEffects.isPending
          }
          onUndo={() => toast.undoItemId && void onUndo(toast.undoItemId)}
          onRetryFailedEffects={() => void onRetryFailedEffects()}
          onRetryLocal={() => {
            if (toast.retryItemId && toast.retryBody) {
              const item = items.find((i) => i.id === toast.retryItemId);
              if (item) {
                void onExecute(item, {
                  ...toast.retryBody,
                  idempotencyKey: crypto.randomUUID(),
                });
              } else setToast(null);
            }
          }}
          onContinue={() => setToast(null)}
          onCancel={() => setToast(null)}
        />
      )}

      <div className="flex min-h-0 flex-1 flex-col md:hidden">
        <div className="mb-3 shrink-0">
          <MobileBucketPicker
            buckets={data.buckets}
            active={bucket}
            onChange={(next) => {
              setBucket(next);
              setSelectedId(null);
            }}
          />
        </div>
        <div className="min-h-0 flex-1">
          {bucketEmpty || !focus ? (
            <EmptyToday
              activeQueueEmpty={activeQueueEmpty}
              bucket={bucket}
              companyName={companyName}
              needsConnection={Boolean(data.setup?.needsConnection)}
              showCompletedProgress
            />
          ) : (
            <MobileFocusCard
              item={focus}
              moveIndex={moveIndex}
              moveTotal={moveTotal}
              busy={execute.isPending || undo.isPending}
              onExecute={(body) => void onExecute(focus, body)}
              onPrev={() => {
                const idx = items.findIndex((i) => i.id === focus.id);
                if (idx > 0) setSelectedId(items[idx - 1]!.id);
              }}
              onNext={() => {
                const idx = items.findIndex((i) => i.id === focus.id);
                if (idx >= 0 && idx < items.length - 1) {
                  setSelectedId(items[idx + 1]!.id);
                }
              }}
            />
          )}
        </div>
      </div>

      <div className="hidden min-h-0 flex-1 flex-col gap-3 overflow-hidden md:flex">
        <div className="shrink-0 space-y-3">
          <ExecutiveSummaryStrip
            metrics={data.metrics}
            activeFilter={bucket}
            onSelect={(hint) => {
              if (isFocusBucket(hint)) setBucket(hint);
            }}
          />

          <FocusBucketSelector
            buckets={data.buckets}
            active={bucket}
            onChange={(next) => {
              setBucket(next);
              setSelectedId(null);
            }}
          />
        </div>

        {bucketEmpty ? (
          <EmptyToday
            activeQueueEmpty={activeQueueEmpty}
            bucket={bucket}
            companyName={companyName}
            needsConnection={Boolean(data.setup?.needsConnection)}
          />
        ) : (
          <div className="grid h-0 min-h-0 flex-1 gap-4 overflow-hidden lg:grid-cols-[minmax(0,1fr)_28rem] xl:grid-cols-[minmax(0,1fr)_34rem]">
            <div className="flex h-full min-h-0 flex-col gap-2 overflow-hidden">
              <section
                ref={listRef}
                onScroll={onListScroll}
                className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1"
              >
                <div
                  className={
                    density === "compact"
                      ? "space-y-1.5 pb-2"
                      : density === "cozy"
                        ? "space-y-2 pb-2"
                        : "space-y-2.5 pb-2"
                  }
                >
                  {pageItems.map((item) => (
                    <CommandItemCard
                      key={item.id}
                      item={item}
                      density={density}
                      selected={selected?.id === item.id}
                      onSelect={() => selectListItem(item.id)}
                    />
                  ))}
                </div>
              </section>

              <TodayListControls
                page={safePage}
                pageSize={pageSize}
                totalItems={items.length}
                density={density}
                onPageChange={changePage}
                onPageSizeChange={changePageSize}
                onDensityChange={changeDensity}
              />
            </div>

            <aside className="h-full min-h-0 overflow-hidden">
              <CommandItemPanel
                item={selected}
                busy={execute.isPending || undo.isPending}
                onExecute={(body) => {
                  if (selected) void onExecute(selected, body);
                }}
              />
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

function localizeMessage(message: string, item: CommandItem): string {
  if (/completed|approved|acknowledged|moved/i.test(message)) {
    return message.includes("CEO Command")
      ? message
      : `${message.replace(/\.$/, "")} in CEO Command.`;
  }
  return `${item.actionDefinition.actionLabel} recorded in CEO Command.`;
}

function ActionToast({
  toast,
  busy,
  onUndo,
  onRetryFailedEffects,
  onRetryLocal,
  onContinue,
  onCancel,
}: {
  toast: ToastState;
  busy?: boolean;
  onUndo: () => void;
  onRetryFailedEffects: () => void;
  onRetryLocal: () => void;
  onContinue: () => void;
  onCancel: () => void;
}) {
  const border =
    toast.kind === "failure"
      ? "border-destructive/40 bg-destructive/10"
      : toast.kind === "partial"
        ? "border-amber-500/40 bg-amber-500/10"
        : "border-border bg-card";

  const canRetryFailed =
    Boolean(toast.dispositionEventId) &&
    toast.effects.some((e) => e.status === "failed");

  return (
    <div
      className={`mb-3 rounded-md border px-3 py-2.5 text-sm text-foreground ${border}`}
    >
      <p className="font-medium">{toast.title}</p>
      {toast.effects.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
          {toast.effects.map((e) => (
            <li key={`${e.label}-${e.status}`}>
              {e.label}:{" "}
              <span
                className={
                  e.status === "failed"
                    ? "font-medium text-amber-600"
                    : "text-muted-foreground"
                }
              >
                  {e.status === "failed"
                    ? "failed"
                    : e.status === "skipped_disconnected"
                      ? "not connected"
                      : e.status === "ok"
                        ? "ok"
                        : "simulated"}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex flex-wrap gap-3">
        {toast.kind === "partial" && (
          <>
            {canRetryFailed && (
              <button
                type="button"
                disabled={busy}
                className="font-medium text-brand hover:underline disabled:opacity-50"
                onClick={onRetryFailedEffects}
              >
                Retry failed effects
              </button>
            )}
            {toast.undoItemId && (
              <button
                type="button"
                disabled={busy}
                className="font-medium text-brand hover:underline disabled:opacity-50"
                onClick={onUndo}
              >
                Undo
              </button>
            )}
            <button
              type="button"
              className="font-medium text-foreground hover:underline"
              onClick={onContinue}
            >
              Continue
            </button>
          </>
        )}
        {toast.kind === "failure" && (
          <>
            <button
              type="button"
              disabled={busy}
              className="font-medium text-brand hover:underline disabled:opacity-50"
              onClick={onRetryLocal}
            >
              Retry
            </button>
            <button
              type="button"
              className="font-medium text-foreground hover:underline"
              onClick={onCancel}
            >
              Cancel
            </button>
          </>
        )}
        {toast.kind === "success" && toast.undoItemId && (
          <button
            type="button"
            disabled={busy}
            className="font-medium text-brand hover:underline disabled:opacity-50"
            onClick={onUndo}
          >
            Undo
          </button>
        )}
      </div>
    </div>
  );
}

function EmptyToday({
  activeQueueEmpty,
  bucket,
  companyName,
  needsConnection = false,
  showCompletedProgress = false,
}: {
  activeQueueEmpty: boolean;
  bucket: FocusBucket;
  companyName?: string;
  needsConnection?: boolean;
  showCompletedProgress?: boolean;
}) {
  if (needsConnection && activeQueueEmpty) {
    return (
      <EmptyBlock
        title="No signals yet"
        body="Connect Apollo, Slack, or External DynamoDB — or push email/CSV signals — in Settings → Connections, then sync to populate Today."
        actionHref="/settings/connections"
        actionLabel="Open Connections"
        showCompletedProgress={false}
      />
    );
  }

  if (companyName && activeQueueEmpty) {
    return (
      <EmptyBlock
        title={`No active moves for ${companyName}`}
        body="There are no executive moves for this company right now."
        showCompletedProgress={showCompletedProgress}
      />
    );
  }

  if (activeQueueEmpty) {
    return (
      <EmptyBlock
        title="All clear for today"
        body="There are no executive moves requiring attention."
        showCompletedProgress={showCompletedProgress}
      />
    );
  }

  return (
    <EmptyBlock
      title={`No items in ${BUCKET_LABELS[bucket]}`}
      body="Other active moves may still be available."
    />
  );
}

function EmptyBlock({
  title,
  body,
  showCompletedProgress = false,
  actionHref,
  actionLabel,
}: {
  title: string;
  body: string;
  showCompletedProgress?: boolean;
  actionHref?: string;
  actionLabel?: string;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card/40 p-10 text-center">
      {showCompletedProgress && (
        <div className="mb-4 w-full max-w-xs">
          <p className="mb-2 text-sm font-medium text-muted-foreground">
            All clear for today
          </p>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted/50">
            <div className="h-full w-full rounded-full bg-brand" />
          </div>
        </div>
      )}
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">{body}</p>
      {actionHref && actionLabel ? (
        <a
          href={actionHref}
          className="mt-4 text-sm font-medium text-brand hover:underline"
        >
          {actionLabel}
        </a>
      ) : null}
    </div>
  );
}
